/**
 * Global Entity Store
 *
 * Single canonical home for cross-space global entities:
 *   - Space        — every Space the user can see (Home, Spaces menu)
 *   - Integration  — and Integration.* hierarchy (org-shared connections)
 *   - MCPConnection — Model Context Protocol servers (used by tool init)
 *   - CLIConnection — command-line tools registered to run on the user's machine
 *   - Org          — the user's organizations
 *
 * These types are needed before any space loads (Home renders Spaces; MCP
 * tool registration runs at boot; Integration UI shows globally) and they
 * are NOT space-scoped, so they cannot live in `spaceEntityStore`.
 *
 * Boot path:
 *   1. App.tsx → initialize() once auth + org context are known.
 *   2. Stage 0 — IDB stale-while-revalidate paint from previous session.
 *   3. Stage 1 — REST burst (parallel typed queries via getGlobalEntities).
 *   4. Live updates flow in via the orgYDocStore live-update bus, dispatched
 *      to this store by entityRegistry.upsert / deleteEntity.
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import { entityService } from '../services/entityService';
import { useDataspaceContextStore } from './dataspaceContextStore';
import { registerScopeAdapter, resolveScope, notifyChange as notifyRegistryChange } from './entityRegistry';
import {
  readGlobalSnapshot,
  writeGlobalSnapshot,
  patchGlobalEntity,
  deleteGlobalEntity,
  deleteGlobalSnapshot,
  PERSONAL_DATASPACE_KEY,
} from '../services/idb/globalEntityIDB';
import { createFileLogger } from '../utils/logger';
import type { BaseEntityType } from '../types/entities';

const log = createFileLogger('GLOBAL_ENTITY_STORE');

const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

export type GlobalEntityLifecycle = 'uninitialized' | 'loading' | 'ready' | 'error';

interface GlobalEntityState {
  /** Map of entityId → entity (covers both _id and id aliases). */
  byId: Map<string, BaseEntityType>;
  /** Map of entityType → array of entityIds — keeps `getByType` O(1). */
  idsByType: Map<string, Set<string>>;
  /** Connected integrations (skeleton.status === 'connected'), by integrationType. */
  connectedIntegrations: Set<string>;
  lifecycle: GlobalEntityLifecycle;
  error?: Error;
  /** dataspace this store is currently scoped to (orgId or null = personal). */
  dataspaceId: string | null;
  /** Monotonic counter to drive React re-renders on Map mutations. */
  bumpVersion: number;
  /** In-flight initialize promise — dedups concurrent boot calls. */
  _initPromise: Promise<void> | null;
  /**
   * Monotonic init epoch. Each successful `initialize()` invocation captures the
   * epoch at start; if `reset()` runs (or a different dataspace re-init takes
   * over) the epoch is bumped, and any in-flight init that observes the new
   * epoch will silently abandon its results instead of clobbering state.
   */
  _initEpoch: number;
}

interface GlobalEntityActions {
  initialize: (dataspaceId: string | null) => Promise<void>;
  reset: () => Promise<void>;
  getById: (id: string) => BaseEntityType | undefined;
  getByType: (entityType: string) => BaseEntityType[];
  upsert: (entity: BaseEntityType) => void;
  deleteEntity: (id: string) => void;
  /** Snapshot of every entity in the store. */
  getAll: () => BaseEntityType[];
}

export type GlobalEntityStore = GlobalEntityState & GlobalEntityActions;

function recomputeConnectedIntegrations(byId: Map<string, BaseEntityType>): Set<string> {
  const connected = new Set<string>();
  for (const e of byId.values()) {
    const t = (e as any).entityType as string | undefined;
    if (!t) continue;
    if (t !== 'Integration' && !t.startsWith('Integration.')) continue;
    if ((e as any).skeleton?.status !== 'connected') continue;
    const integrationType = (e as any).skeleton?.integrationType;
    if (integrationType) connected.add(integrationType);
  }
  return connected;
}

function indexEntities(entities: BaseEntityType[]): {
  byId: Map<string, BaseEntityType>;
  idsByType: Map<string, Set<string>>;
} {
  const byId = new Map<string, BaseEntityType>();
  const idsByType = new Map<string, Set<string>>();
  for (const e of entities) {
    if (!e || resolveScope(e.entityType) !== 'global') continue;
    const id = e._id;
    if (!id) continue;
    byId.set(id, e);
    if ((e as any).id && (e as any).id !== id) byId.set((e as any).id, e);
    let ids = idsByType.get(e.entityType);
    if (!ids) {
      ids = new Set<string>();
      idsByType.set(e.entityType, ids);
    }
    ids.add(id);
  }
  return { byId, idsByType };
}

export const useGlobalEntityStore = create<GlobalEntityStore>((set, get) => ({
  byId: new Map(),
  idsByType: new Map(),
  connectedIntegrations: new Set(),
  lifecycle: 'uninitialized',
  dataspaceId: null,
  bumpVersion: 0,
  _initPromise: null,
  _initEpoch: 0,

  initialize: async (dataspaceId: string | null) => {
    const current = get();

    // If already initialized for this dataspace, skip.
    if (current.lifecycle === 'ready' && current.dataspaceId === dataspaceId) {
      return;
    }

    // Dedup concurrent inits for the same dataspace before any async work runs.
    if (current._initPromise && current.dataspaceId === dataspaceId) {
      return current._initPromise;
    }

    // Different dataspace — reset before re-initializing so consumers don't see stale data.
    if (current.dataspaceId !== null && current.dataspaceId !== dataspaceId) {
      await get().reset();
    }

    // Bump the epoch and capture it for the IIFE. Any set() inside the promise
    // that observes a different epoch knows it has been superseded by a newer
    // initialize() / reset() and bails without mutating state.
    const epoch = get()._initEpoch + 1;
    set({ _initEpoch: epoch, lifecycle: 'loading', dataspaceId });
    const isStale = (): boolean => get()._initEpoch !== epoch;

    const promise = (async () => {
      const t0 = performance.now();
      const idbKey = dataspaceId || PERSONAL_DATASPACE_KEY;

      try {
        // Stage 0 — paint from IDB if a fresh-enough snapshot exists.
        try {
          const snapshot = await readGlobalSnapshot(idbKey);
          if (isStale()) return;
          if (
            snapshot &&
            Date.now() - snapshot.meta.fetchedAt <= STALE_AFTER_MS
          ) {
            const { byId, idsByType } = indexEntities(snapshot.entities);
            set((s) => ({
              byId,
              idsByType,
              connectedIntegrations: recomputeConnectedIntegrations(byId),
              bumpVersion: s.bumpVersion + 1,
            }));
            log.info(
              `[GlobalEntityStore] 🪟 idbHit count=${snapshot.entities.length} fetchedAt=${snapshot.meta.fetchedAt}`,
            );
          }
        } catch (idbErr) {
          log.warn('[GlobalEntityStore] IDB paint failed (non-fatal)', idbErr);
        }

        if (isStale()) return;

        // Stage 1 — REST refresh (always runs to catch additions since last snapshot).
        const entities = await entityService.getGlobalEntities();
        if (isStale()) {
          log.info(
            `[GlobalEntityStore] init epoch=${epoch} stale after REST — discarding ${entities.length} entities`,
          );
          return;
        }
        const { byId, idsByType } = indexEntities(entities);
        set((s) => ({
          byId,
          idsByType,
          connectedIntegrations: recomputeConnectedIntegrations(byId),
          lifecycle: 'ready',
          error: undefined,
          bumpVersion: s.bumpVersion + 1,
        }));

        // Persist to IDB for next session's stale paint. Fire-and-forget.
        writeGlobalSnapshot(idbKey, entities).catch((err) =>
          log.warn('[GlobalEntityStore] IDB write failed', err),
        );

        log.info(
          `[GlobalEntityStore] ✅ initialize dataspaceId=${dataspaceId ?? 'personal'} count=${entities.length} ms=${(performance.now() - t0).toFixed(0)}`,
        );
      } catch (err) {
        if (isStale()) return;
        log.error('[GlobalEntityStore] ❌ initialize failed', err);
        set({ lifecycle: 'error', error: err as Error });
        throw err;
      } finally {
        if (!isStale()) {
          set({ _initPromise: null });
        }
      }
    })();

    set({ _initPromise: promise });
    return promise;
  },

  reset: async () => {
    const prev = get().dataspaceId;
    set((s) => ({
      byId: new Map(),
      idsByType: new Map(),
      connectedIntegrations: new Set(),
      lifecycle: 'uninitialized',
      dataspaceId: null,
      bumpVersion: s.bumpVersion + 1,
      _initPromise: null,
      _initEpoch: s._initEpoch + 1,
      error: undefined,
    }));
    if (prev !== null) {
      await deleteGlobalSnapshot(prev || PERSONAL_DATASPACE_KEY).catch(() => {});
    }
  },

  getById: (id: string) => {
    if (!id) return undefined;
    return get().byId.get(id);
  },

  getByType: (entityType: string) => {
    const ids = get().idsByType.get(entityType);
    if (!ids || ids.size === 0) return [];
    const byId = get().byId;
    const out: BaseEntityType[] = [];
    for (const id of ids) {
      const e = byId.get(id);
      if (e) out.push(e);
    }
    return out;
  },

  getAll: () => {
    // De-dup the byId map (which holds both _id and id aliases for some entities).
    const seen = new Set<string>();
    const out: BaseEntityType[] = [];
    for (const e of get().byId.values()) {
      const id = e._id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(e);
    }
    return out;
  },

  upsert: (entity: BaseEntityType) => {
    if (!entity || !entity._id) return;
    if (resolveScope(entity.entityType) !== 'global') return;
    set((s) => {
      const byId = new Map(s.byId);
      const idsByType = new Map(s.idsByType);

      const prev = byId.get(entity._id);
      const prevType = prev?.entityType;
      const typeBucketChanged = !prevType || prevType !== entity.entityType;

      byId.set(entity._id, entity);
      if ((entity as any).id && (entity as any).id !== entity._id) {
        byId.set((entity as any).id, entity);
      }

      // Type bucket bookkeeping: remove from old bucket if entityType changed.
      if (prevType && prevType !== entity.entityType) {
        const oldIds = idsByType.get(prevType);
        if (oldIds) {
          const next = new Set(oldIds);
          next.delete(entity._id);
          if (next.size === 0) idsByType.delete(prevType);
          else idsByType.set(prevType, next);
        }
      }

      // Only re-allocate the type-bucket Set when membership actually changes.
      // Updates that touch only the entity body (skeleton, name, etc.) keep the
      // same Set ref so type-filtered selectors don't re-render unnecessarily.
      if (typeBucketChanged) {
        const existingIds = idsByType.get(entity.entityType);
        if (!existingIds) {
          const ids = new Set<string>();
          ids.add(entity._id);
          idsByType.set(entity.entityType, ids);
        } else if (!existingIds.has(entity._id)) {
          const ids = new Set(existingIds);
          ids.add(entity._id);
          idsByType.set(entity.entityType, ids);
        }
      }

      return {
        byId,
        idsByType,
        connectedIntegrations: recomputeConnectedIntegrations(byId),
        bumpVersion: s.bumpVersion + 1,
      };
    });

    // Persist to IDB. Fire-and-forget. Only write once we have a dataspace
    // identity — pre-init upserts (e.g. tests, edge timing) must not persist
    // to the wrong key.
    const dataspaceId = get().dataspaceId;
    if (dataspaceId !== null || get().lifecycle === 'ready') {
      patchGlobalEntity(dataspaceId || PERSONAL_DATASPACE_KEY, entity).catch(() => {});
    }
  },

  deleteEntity: (id: string) => {
    if (!id) return;
    const prev = get().byId.get(id);
    if (!prev) return;
    set((s) => {
      const byId = new Map(s.byId);
      const idsByType = new Map(s.idsByType);
      byId.delete(id);
      if ((prev as any).id && (prev as any).id !== id) byId.delete((prev as any).id);
      const ids = idsByType.get(prev.entityType);
      if (ids) {
        const next = new Set(ids);
        next.delete(id);
        if (next.size === 0) idsByType.delete(prev.entityType);
        else idsByType.set(prev.entityType, next);
      }
      return {
        byId,
        idsByType,
        connectedIntegrations: recomputeConnectedIntegrations(byId),
        bumpVersion: s.bumpVersion + 1,
      };
    });

    const dataspaceId = get().dataspaceId;
    if (dataspaceId !== null || get().lifecycle === 'ready') {
      deleteGlobalEntity(dataspaceId || PERSONAL_DATASPACE_KEY, id).catch(() => {});
    }
  },
}));

// Stable empty-array reference for the React selector — see spaceEntityStore
// for why this matters (Object.is comparison vs new [] on every render).
const EMPTY_ENTITIES: BaseEntityType[] = [];

/**
 * React hook — returns all global entities of a specific type.
 *
 * Snapshot stability: zustand selectors must return a stable identity so
 * React's `useSyncExternalStore` snapshot cache holds across renders. We
 * subscribe to the type's id-set (kept ref-stable across non-mutating
 * updates by `upsert`/`deleteEntity`) and the byId Map (replaced wholesale
 * only on mutations), then build the result array inside `useMemo`.
 */
export function useGlobalEntitiesByType(entityType: string): BaseEntityType[] {
  const idsRef = useGlobalEntityStore((s) => s.idsByType.get(entityType));
  const byIdRef = useGlobalEntityStore((s) => s.byId);

  return useMemo(() => {
    if (!idsRef || idsRef.size === 0) return EMPTY_ENTITIES;
    const out: BaseEntityType[] = [];
    for (const id of idsRef) {
      const e = byIdRef.get(id);
      if (e) out.push(e);
    }
    return out.length === 0 ? EMPTY_ENTITIES : out;
  }, [idsRef, byIdRef]);
}

/** React hook — returns every global entity regardless of type. */
export function useGlobalEntities(): BaseEntityType[] {
  const byIdRef = useGlobalEntityStore((s) => s.byId);

  return useMemo(() => {
    if (byIdRef.size === 0) return EMPTY_ENTITIES;
    const seen = new Set<string>();
    const out: BaseEntityType[] = [];
    for (const e of byIdRef.values()) {
      const id = e._id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(e);
    }
    return out.length === 0 ? EMPTY_ENTITIES : out;
  }, [byIdRef]);
}

/** React hook — returns the connected-integration types Set (memoized by store). */
export function useConnectedIntegrations(): Set<string> {
  return useGlobalEntityStore((s) => s.connectedIntegrations);
}

// Auto-react to org switches: when the dataspace context changes, the global
// store must reload from REST/IDB for the new org. We subscribe imperatively
// (no React mount needed) so the store stays self-managing.
if (typeof window !== 'undefined') {
  let prevOrgId: string | null = useDataspaceContextStore.getState().currentContext.orgId ?? null;
  useDataspaceContextStore.subscribe((state) => {
    const nextOrgId = state.currentContext.orgId ?? null;
    if (nextOrgId === prevOrgId) return;
    prevOrgId = nextOrgId;
    log.info(`[GlobalEntityStore] org switch detected → ${nextOrgId ?? 'personal'}, re-initializing`);
    useGlobalEntityStore
      .getState()
      .initialize(nextOrgId)
      .catch((err) => log.warn('[GlobalEntityStore] re-init after org switch failed', err));
  });
}

// Register the adapter with the registry so the orgYDocStore live-update bus
// and entityRegistry.getById/getByType/upsert/deleteEntity dispatch into us.
registerScopeAdapter('global', {
  getById: (id) => useGlobalEntityStore.getState().getById(id),
  getByType: (entityType) => useGlobalEntityStore.getState().getByType(entityType),
  upsert: (entity) => useGlobalEntityStore.getState().upsert(entity),
  deleteEntity: (id) => useGlobalEntityStore.getState().deleteEntity(id),
  dumpAll: () => useGlobalEntityStore.getState().getAll(),
});

// Bridge bumpVersion ticks → entityRegistry.subscribeToChanges so app stores
// can listen for global entity changes (Spaces / Integrations / MCPConnections)
// via the registry without subscribing directly to this store.
useGlobalEntityStore.subscribe((state, prev) => {
  if (
    state.bumpVersion === prev.bumpVersion &&
    state.lifecycle === prev.lifecycle &&
    state.connectedIntegrations === prev.connectedIntegrations
  ) {
    return;
  }
  notifyRegistryChange();
});
